import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useToast } from '@/shared/hooks/use-toast';
import { handleApiError } from '@/shared/api/requests';
import {
  usePunchWorkflowMutations,
  usePunchListMutations,
  usePunchWorkflowBatches,
  usePunchWorkflowRuns,
  usePunchItemWorkflowTemplates,
  useProjectAreaTags,
  useTradePartners,
  useTrades,
} from '../hooks';
import type { PunchItemWorkflow } from '../types';
import { PunchListsNavigation } from './PunchListsNavigation';
import { PunchWorkflowBatchDialog } from './PunchWorkflowBatchDialog';
import {
  workflowConcepts,
  WorkflowRunsConceptView,
  WorkflowTemplatesConceptView,
} from './PunchWorkflowConceptViews';
import { PunchWorkflowLaunchDialog } from './PunchWorkflowLaunchDialog';
import { PunchItemWorkflowDialog } from './PunchItemWorkflowDialog';

export const PunchWorkflowsPage: React.FC = () => {
  const { clientName } = useParams<{ clientName?: string }>();
  const { projectIdNumber: projectId } = useCurrentProject();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [defaultSaveAsTemplate, setDefaultSaveAsTemplate] = useState(false);
  const [launchDialogOpen, setLaunchDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<PunchItemWorkflow | null>(null);
  const [launchWorkflow, setLaunchWorkflow] = useState<PunchItemWorkflow | null>(null);
  const [batchWorkflow, setBatchWorkflow] = useState<PunchItemWorkflow | null>(null);
  const [activeTab, setActiveTab] = useState('runs');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [bulkActionPending, setBulkActionPending] = useState(false);

  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = usePunchItemWorkflowTemplates(projectId);
  const { data: runs = [], isLoading: runsLoading, error: runsError } = usePunchWorkflowRuns(projectId);
  const { data: batches = [], isLoading: batchesLoading, error: batchesError } = usePunchWorkflowBatches(projectId);
  const { data: areaTags = [] } = useProjectAreaTags(projectId);
  const { data: trades = [] } = useTrades();
  const { data: tradePartners = [] } = useTradePartners(projectId);
  const mutations = usePunchWorkflowMutations(projectId);
  const punchItemMutations = usePunchListMutations(projectId);
  const workflowItems = useMemo(
    () => runs.flatMap((run) => run.items),
    [runs],
  );
  const selectedItems = useMemo(
    () => workflowItems.filter((item) => selectedItemIds.has(item.id)),
    [selectedItemIds, workflowItems],
  );

  useEffect(() => {
    const availableIds = new Set(workflowItems.map((item) => item.id));
    setSelectedItemIds((current) => {
      const next = new Set([...current].filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [workflowItems]);

  if (!projectId) {
    return <BaseLayout><div className="p-6 text-sm text-muted-foreground">Select a project to view workflows.</div></BaseLayout>;
  }

  const punchBase = clientName
    ? `/app/property/${clientName}/${projectId}/punch-lists`
    : `/app/property/${projectId}/punch-lists`;

  const openCreate = (saveAsTemplate: boolean) => {
    setEditingWorkflow(null);
    setDefaultSaveAsTemplate(saveAsTemplate);
    setWorkflowDialogOpen(true);
  };

  const openEdit = (workflow: PunchItemWorkflow) => {
    setEditingWorkflow(workflow);
    setWorkflowDialogOpen(true);
  };

  const openLaunch = (workflow: PunchItemWorkflow) => {
    setLaunchWorkflow(workflow);
    setLaunchDialogOpen(true);
  };

  const openBatch = (workflow: PunchItemWorkflow) => {
    setBatchWorkflow(workflow);
    setBatchDialogOpen(true);
  };

  const selectItem = (id: string, checked: boolean) => {
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectWorkflowItems = (ids: string[], checked: boolean) => {
    setSelectedItemIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const runBulkAction = async (action: 'complete' | 'delete') => {
    if (selectedItems.length === 0 || bulkActionPending) return;
    if (
      action === 'delete'
      && !window.confirm(
        `Delete ${selectedItems.length} selected punch ${selectedItems.length === 1 ? 'item' : 'items'}? This cannot be undone.`,
      )
    ) return;

    setBulkActionPending(true);
    const succeeded = new Set<string>();
    const failed: string[] = [];
    for (const item of selectedItems) {
      try {
        if (action === 'delete') {
          await punchItemMutations.deleteItem.mutateAsync(item.id);
        } else if (item.status !== 'COMPLETED' && item.status !== 'CLOSED') {
          await punchItemMutations.updateItem.mutateAsync({
            id: item.id,
            data: { status: 'COMPLETED' },
          });
        }
        succeeded.add(item.id);
      } catch {
        failed.push(item.id);
      }
    }
    setSelectedItemIds((current) => new Set(
      [...current].filter((id) => !succeeded.has(id)),
    ));
    setBulkActionPending(false);

    if (failed.length > 0) {
      toast({
        title: `Couldn’t ${action === 'delete' ? 'delete' : 'complete'} every selected item`,
        description: `${failed.length} ${failed.length === 1 ? 'item needs' : 'items need'} attention. The remaining selection was kept.`,
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: action === 'delete' ? 'Punch items deleted' : 'Punch items marked complete',
      description: `${succeeded.size} ${succeeded.size === 1 ? 'item was' : 'items were'} updated.`,
    });
  };

  const conceptCopy = workflowConcepts['job-board'];

  const archive = async (workflow: PunchItemWorkflow) => {
    if (!window.confirm(`Archive “${workflow.name}”? Work that has already started will not be changed.`)) return;
    try {
      await mutations.archiveWorkflow.mutateAsync(workflow.id);
      toast({ title: 'Workflow template archived' });
    } catch (error) {
      toast({ title: 'Unable to archive workflow template', description: handleApiError(error), variant: 'destructive' });
    }
  };

  return (
    <BaseLayout>
      <div className="min-w-0 space-y-6 p-4 md:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Punch Lists</p>
            <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em]">{conceptCopy.pageTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{conceptCopy.pageDescription}</p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'runs' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={selectedItems.length === 0 || bulkActionPending}
                  >
                    {bulkActionPending
                      ? <Loader2 className="mr-2 size-4 animate-spin" />
                      : <ChevronDown className="mr-2 size-4" />}
                    Actions
                    {selectedItems.length > 0 && (
                      <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary">
                        {selectedItems.length}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onSelect={() => void runBulkAction('complete')}>
                    <CheckCircle2 className="mr-2 size-4" /> Mark Complete
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => void runBulkAction('delete')}
                  >
                    <Trash2 className="mr-2 size-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button onClick={() => openCreate(activeTab === 'templates')}>
              <Plus className="mr-2 size-4" /> {activeTab === 'templates' ? conceptCopy.createTemplate : conceptCopy.createRun}
            </Button>
          </div>
        </div>

        <PunchListsNavigation projectId={projectId} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
          <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-[13px] border border-border bg-card p-1.5 shadow-slate sm:w-auto">
            <TabsTrigger value="runs">
              {conceptCopy.runsTab} <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{runs.length}</span>
            </TabsTrigger>
            <TabsTrigger value="templates">
              {conceptCopy.templatesTab} <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{templates.length}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="runs" className="space-y-6">
            {(runsLoading || batchesLoading) && <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading workflows…</div>}
            {(runsError || batchesError) && <Card><CardContent className="p-6 text-sm text-destructive">We couldn’t load the workflows for this project.</CardContent></Card>}
            {!runsLoading && !batchesLoading && !runsError && !batchesError && (
              <WorkflowRunsConceptView
                concept="job-board"
                runs={runs}
                batches={batches}
                areaTags={areaTags}
                onOpenItem={(id) => navigate(`${punchBase}/${id}`)}
                selectedItemIds={selectedItemIds}
                onSelectItem={selectItem}
                onSelectWorkflow={selectWorkflowItems}
              />
            )}
          </TabsContent>

          <TabsContent value="templates" className="space-y-4">
            {templatesLoading && <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading workflow templates…</div>}
            {templatesError && <Card><CardContent className="p-6 text-sm text-destructive">We couldn’t load the workflow templates for this project.</CardContent></Card>}
            {!templatesLoading && !templatesError && (
              <WorkflowTemplatesConceptView
                concept="job-board"
                templates={templates}
                onLaunch={openLaunch}
                onBatch={openBatch}
                onEdit={openEdit}
                onArchive={(workflow) => void archive(workflow)}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <PunchItemWorkflowDialog
        open={workflowDialogOpen}
        onOpenChange={setWorkflowDialogOpen}
        projectId={projectId}
        workflow={editingWorkflow}
        defaultSaveAsTemplate={defaultSaveAsTemplate}
        heading={editingWorkflow
          ? 'Edit Workflow Template'
          : activeTab === 'templates' ? conceptCopy.createTemplate : conceptCopy.createRun}
        trades={trades}
        tradePartners={tradePartners}
        onSubmit={async (data) => {
          if (data.mode === 'update' && editingWorkflow) {
            const result = await mutations.updateWorkflow.mutateAsync({
              id: editingWorkflow.id,
              data: data.definition,
            });
            setActiveTab('templates');
            return result;
          }
          if (data.mode !== 'start') return;
          const defaults = {
            punch_manager: data.punchManager,
            final_approver: data.finalApprover,
            is_private: data.isPrivate,
          };
          if (data.locationMode === 'single') {
            const result = await mutations.launchRun.mutateAsync({
              ...defaults,
              workflow: null,
              location: data.locations[0],
              name: data.definition.name,
              description: data.definition.description,
              is_template: data.definition.is_template,
              steps: data.definition.steps,
            });
            setActiveTab('runs');
            toast({ title: 'Workflow started', description: 'The first task is ready for the crew.' });
            return result;
          }
          if (data.locationMode !== 'multiple') return;
          const result = await mutations.launchBatch.mutateAsync({
            ...defaults,
            workflow: null,
            workflow_name: data.definition.name,
            workflow_description: data.definition.description,
            is_template: data.definition.is_template,
            steps: data.definition.steps,
            name: `${data.definition.name} — ${data.locations.length} locations`,
            locations: data.locations,
          });
          setActiveTab('runs');
          toast({
            title: 'Workflow started',
            description: `${data.locations.length} locations will move through each handoff together.`,
          });
          return result;
        }}
      />
      <PunchWorkflowLaunchDialog
        open={launchDialogOpen}
        onOpenChange={setLaunchDialogOpen}
        projectId={projectId}
        workflow={launchWorkflow}
        onSubmit={async (data) => {
          const result = await mutations.launchRun.mutateAsync(data);
          setActiveTab('runs');
          return result;
        }}
      />
      <PunchWorkflowBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        projectId={projectId}
        workflow={batchWorkflow}
        onSubmit={async (data) => {
          const result = await mutations.launchBatch.mutateAsync(data);
          setActiveTab('runs');
          return result;
        }}
      />
    </BaseLayout>
  );
};

export default PunchWorkflowsPage;
